import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { siteContent } from '../../data/products';
import { useCart } from '../../context/CartContext';
import Icon from '../Icon/Icon';
import './Navbar.css';

/* Centered-logo navbar (Dadu's-style): links split left/right around the
 * badge, cart on the far right, hamburger + drawer on mobile. */

/* Anything focusable that can live inside the drawer */
const FOCUSABLE = 'button, a[href], [tabindex]:not([tabindex="-1"])';

const Navbar = () => {
  const reduce = useReducedMotion();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { count, setOpen } = useCart();

  const hamburgerRef = useRef(null);
  const drawerRef = useRef(null);
  const navigation = siteContent.navigation;
  const leftLinks = navigation.links.filter((link) => link.placement === 'left');
  const rightLinks = navigation.links.filter((link) => link.placement === 'right');
  const mobileLinks = navigation.links.filter((link) => link.mobile);

  /* rAF-throttled + passive: the raw listener fired on every scroll tick and
     called setState each time, which is exactly what thrashes on mobile. */
  useEffect(() => {
    let frame = 0;
    const apply = () => {
      frame = 0;
      setScrolled(window.scrollY > 50);
    };
    const handleScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollTo = useCallback((...ids) => {
    const target = ids.map((id) => document.getElementById(id)).find(Boolean);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    } else {
      /* Last-ditch fallback — never leave a nav click doing nothing at all */
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`[Navbar] no section found for: ${ids.join(', ')}`);
      }
    }
    setMenuOpen(false);
  }, []);

  /* Drawer: scroll lock, focus move-in / restore, Escape, and a focus cycle.
     The links themselves are only rendered while open, so nothing off-screen
     can ever collect a Tab stop. */
  useEffect(() => {
    if (!menuOpen) return undefined;
    const trigger = hamburgerRef.current;
    const drawer = drawerRef.current;

    drawer?.querySelector(FOCUSABLE)?.focus();
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        return;
      }
      if (e.key !== 'Tab' || !drawer) return;
      const items = drawer.querySelectorAll(FOCUSABLE);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    /* Tapping the page behind the drawer closes it, the way a sheet should */
    const onPointerDown = (e) => {
      if (drawer?.contains(e.target) || trigger?.contains(e.target)) return;
      setMenuOpen(false);
    };
    /* Above 900px the drawer is display:none — leaving it "open" would strand
       the scroll lock on a panel nobody can see. */
    const onResize = () => {
      if (window.innerWidth > 900) setMenuOpen(false);
    };

    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('resize', onResize);
      document.body.style.overflow = '';
      trigger?.focus();
    };
  }, [menuOpen]);

  const renderLinks = (links) =>
    links.map(({ id, label, target, fallbackTargets, emphasis }) => (
      <button
        key={id}
        className={`nav-link ${emphasis ? 'nav-order-link' : ''}`}
        onClick={() => scrollTo(target, ...fallbackTargets)}
      >
        {label}
        {emphasis && <Icon name="arrow" size={16} strokeWidth={1.8} />}
      </button>
    ));

  return (
    <motion.nav
      className={`navbar ${scrolled ? 'scrolled' : ''}`}
      aria-label="Primary navigation"
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
    >
      <div className="nav-side nav-side-left">{renderLinks(leftLinks)}</div>

      <button
        className="navbar-logo"
        onClick={() => scrollTo('hero')}
        aria-label={`${siteContent.brand.name} — back to top`}
      >
        <img src={siteContent.brand.logo} alt={siteContent.brand.logoAlt} />
      </button>

      <button
        className="nav-mobile-order"
        onClick={() => scrollTo(navigation.mobileCtaTarget, ...navigation.mobileCtaFallbackTargets)}
      >
        <span>{navigation.mobileCtaLabel}</span>
        <Icon name="arrow" size={16} strokeWidth={1.9} />
      </button>

      <div className="nav-side nav-side-right">
        {renderLinks(rightLinks)}
        <motion.button
          className="nav-cart"
          onClick={() => {
            /* Close the drawer first — two panels sharing one body scroll
               lock is how the lock ends up released while one is still open */
            setMenuOpen(false);
            setOpen(true);
          }}
          aria-label={count > 0 ? `Open cart, ${count} ${count === 1 ? 'item' : 'items'}` : 'Open empty cart'}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.88 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        >
          <Icon name="cart" size={21} strokeWidth={1.7} />
          <AnimatePresence>
            {count > 0 && (
              <motion.span
                key={count}
                className="nav-cart-badge"
                initial={{ scale: 0.3, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.3, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 18 }}
                aria-hidden="true"
              >
                {count}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>

      <button
        ref={hamburgerRef}
        className={`hamburger ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen((v) => !v)}
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
        aria-controls="nav-drawer"
      >
        <span /><span /><span />
      </button>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            id="nav-drawer"
            ref={drawerRef}
            className="nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            initial={reduce ? { opacity: 0 } : { y: '-130%', opacity: 0 }}
            animate={reduce ? { opacity: 1 } : { y: 0, opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { y: '-130%', opacity: 0 }}
            transition={{ duration: reduce ? 0.15 : 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            {renderLinks(mobileLinks)}
            <button className="nav-drawer-close" onClick={() => setMenuOpen(false)}>
              Close
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
export default Navbar;
